import { useEffect, useState } from "react";
import useTitleStore from "../../store/titleStore";
import useDebounce from "../../hooks/useDebounce";
import { useTableHeight } from "../../hooks/useTableHeight";
import { useNavigate } from "react-router";
import {
  Button,
  Card,
  Flex,
  Form,
  Grid,
  Input,
  message,
  Pagination,
  Select,
  Table,
  Tag,
} from "antd";
import { Icon } from "@iconify/react/dist/iconify.js";
import { standMeterService } from "../../services/stand-meter.service";
import { keepPreviousData, useQuery, useMutation } from "@tanstack/react-query";
import { downloadBlob } from "../../utils/download";
import { format } from "date-fns";

export default function StandMeterIndex() {
  const breakpoint = Grid.useBreakpoint();
  const navigate = useNavigate();
  const { setTitle } = useTitleStore();
  const tableHeight = useTableHeight();

  const [messageApi, messageHolder] = message.useMessage();
  const savedState = JSON.parse(sessionStorage.getItem('standMeterState') || '{}');

  const [keyword, setKeyword] = useState(savedState.keyword || "");
  const debouncedKeyword = useDebounce(keyword, 500);
  const [filterForm] = Form.useForm();

  const monthMap = {
    "01": "January",
    "02": "February",
    "03": "March",
    "04": "April",
    "05": "May",
    "06": "June",
    "07": "July",
    "08": "August",
    "09": "September",
    10: "October",
    11: "November",
    12: "December",
  };

  const currentMonth =
    monthMap[String(new Date().getMonth() + 1).padStart(2, "0")];
  const currentYear = String(new Date().getFullYear());

  const initialFormValues = {
    month: savedState.formValues?.month || String(new Date().getMonth() + 1).padStart(2, "0"),
    year: savedState.formValues?.year || currentYear,
    status: savedState.formValues?.status || null,
  };

  const [dataParams, setDataParams] = useState(savedState.dataParams || {
    limit: 10,
    page: 1,
    month: monthMap[initialFormValues.month] || currentMonth,
    year: initialFormValues.year,
    keyword: savedState.keyword || "",
    status: initialFormValues.status,
  });

  useEffect(() => {
    setTitle("Stand Meter");
  }, [setTitle]);

  useEffect(() => {
    filterForm.setFieldsValue(initialFormValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const month = Form.useWatch("month", filterForm);
  const year = Form.useWatch("year", filterForm);
  const status = Form.useWatch("status", filterForm);

  useEffect(() => {
    const monthValue = month || filterForm.getFieldValue('month');
    const yearValue = year || filterForm.getFieldValue('year');
    const statusValue = status !== undefined ? status : filterForm.getFieldValue('status');

    if (!monthValue || !yearValue) return;

    const monthLabel = monthMap[monthValue] || currentMonth;

    setDataParams((prev) => ({
      ...prev,
      month: monthLabel,
      year: yearValue,
      keyword: debouncedKeyword || "",
      status: statusValue || null,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedKeyword, month, year, status]);

  useEffect(() => {
    sessionStorage.setItem('standMeterState', JSON.stringify({
      keyword,
      formValues: {
        month: filterForm.getFieldValue('month'),
        year: filterForm.getFieldValue('year'),
        status: filterForm.getFieldValue('status'),
      },
      dataParams
    }));
  }, [keyword, dataParams, filterForm]);

  const {
    data: standMeterData,
    isLoading,
    isRefetching,
  } = useQuery({
    queryKey: ["standMeter", dataParams],
    queryFn: () => standMeterService.getAllData(dataParams),
    placeholderData: keepPreviousData,
  });

  const handleChangePage = (page, pageSize) => {
    setDataParams((prev) => ({
      ...prev,
      limit: pageSize,
      page,
    }));
  };

  const exportMutation = useMutation({
    mutationFn: (val) => standMeterService.export(val),
    onSuccess: ({ blob, filename }) => {
      downloadBlob(blob, filename);
      messageApi.success("Data berhasil diexport");
    },
    onError: (err) => {
      messageApi.error("Gagal mengexport data");
      console.error(err);
    }
  });

  const mappedData =
    standMeterData?.data?.map((item) => ({
      ...item,
      name: item.Nomen?.name || "-",
      nomen: item.Nomen?.nomen || "-",
      month_label: item.month || "-",
      createdAt: item.inputDate || item.createdAt,
      index: item.index,
      regional: item.Nomen?.area || "-",
      ab: item.Nomen?.ab || "-",
      status: item.status,
    })) || [];

  const columns = [
    {
      title: "No.",
      align: "center",
      width: 80,
      fixed: breakpoint.md && "left",
      render: (text, record, index) => index + 1,
    },
    {
      title: "Nama Pelanggan",
      dataIndex: "Nomen",
      width: 180,
      fixed: breakpoint.md && "left",
      render: (_, record) => (
        <div>
          <strong>{record.name}</strong> <br /> {record.nomen}
        </div>
      ),
    },
    {
      title: "Bulan",
      dataIndex: "month_label",
      width: 150,
      render: (val) => {
        const idnMonths = {
          January: "Januari",
          February: "Februari",
          March: "Maret",
          April: "April",
          May: "Mei",
          June: "Juni",
          July: "Juli",
          August: "Agustus",
          September: "September",
          October: "Oktober",
          November: "November",
          December: "Desember",
        };
        return idnMonths[val] || val;
      },
    },
    {
      title: "Tanggal Input",
      dataIndex: "inputDate",
      width: 180,
      render: (val) => val && format(new Date(val), "dd/MM/yyyy"),
    },
    { title: "Index", dataIndex: "index", width: 100 },
    { title: "Regional", dataIndex: "regional", width: 130 },
    { title: "AB", dataIndex: "ab", width: 150 },
    {
      title: "Status",
      dataIndex: "status",
      width: 140,
      render: (val) => {
        if (val === "updated") return <Tag color="green">Updated</Tag>;
        if (val === "cancel") return <Tag color="red">Cancel</Tag>;
        if (val === "in_progress") return <Tag color="yellow">In Progress</Tag>;
        return "-";
      },
    },
    {
      className: "last-cell-p",
      title: "Aksi",
      align: "right",
      width: 80,
      fixed: "right",
      render: (record) => (
        <Button
          icon={<Icon icon="solar:eye-bold-duotone" width="18" height="18" />}
          color="primary"
          variant="filled"
          onClick={() => navigate("/stand-meter/detail/" + record.id)}
        />
      ),
    },
  ];

  return (
    <>
      {messageHolder}
      <Card
        styles={{
          body: {
            paddingLeft: 0,
            paddingRight: 0,
          },
        }}
      >
        <Form form={filterForm} initialValues={initialFormValues}>
          <Flex
            justify={!breakpoint.lg ? "start" : "space-between"}
            align={!breakpoint.lg ? "start" : "center"}
            vertical={!breakpoint.lg}
            gap={!breakpoint.lg ? 16 : 0}
            style={{ paddingInline: 24, marginBottom: 24 }}
          >
            <Input
              style={{ width: 250 }}
              prefix={
                <Icon icon="hugeicons:search-01" width="16" height="16" />
              }
              placeholder="Cari"
              onChange={(e) => setKeyword(e.target.value)}
              value={keyword}
            />

            <Flex gap={20}>
              <Form.Item noStyle name="month">
                <Select
                  allowClear
                  placeholder="Bulan"
                  style={{ width: 150 }}
                  options={[
                    { label: "Januari", value: "01" },
                    { label: "Februari", value: "02" },
                    { label: "Maret", value: "03" },
                    { label: "April", value: "04" },
                    { label: "Mei", value: "05" },
                    { label: "Juni", value: "06" },
                    { label: "Juli", value: "07" },
                    { label: "Agustus", value: "08" },
                    { label: "September", value: "09" },
                    { label: "Oktober", value: "10" },
                    { label: "November", value: "11" },
                    { label: "Desember", value: "12" },
                  ]}
                />
              </Form.Item>

              <Form.Item noStyle name="year">
                <Select
                  allowClear
                  placeholder="Tahun"
                  style={{ width: 120 }}
                  options={Array.from({ length: 6 }, (_, i) => {
                    const year = new Date().getFullYear() - i;
                    return { label: `${year}`, value: `${year}` };
                  })}
                />
              </Form.Item>

              <Form.Item noStyle name="status">
                <Select
                  allowClear
                  placeholder="Status"
                  style={{ width: 150 }}
                  options={[
                    { label: "In Progress", value: "in_progress" },
                    { label: "Updated", value: "updated" },
                    { label: "Cancel", value: "cancel" },
                  ]}
                />
              </Form.Item>

              <Button
                variant="solid"
                color="primary"
                loading={exportMutation.isPending}
                onClick={() => exportMutation.mutate(dataParams)}
              >
                Export
              </Button>
            </Flex>
          </Flex>
        </Form>

        <Table
          id="stand-meter-table"
          columns={columns}
          size="small"
          loading={isLoading || isRefetching}
          rowKey={"id"}
          scroll={{ y: tableHeight }}
          dataSource={mappedData || []}
          pagination={false}
        />

        <Pagination
          style={{ marginInline: 24, marginTop: 16 }}
          total={standMeterData?.pagination?.total || 0}
          showSizeChanger
          pageSizeOptions={["10", "20", "50"]}
          current={standMeterData?.pagination?.page || 1}
          pageSize={dataParams.limit}
          align="end"
          onChange={handleChangePage}
        />
      </Card>
    </>
  );
}
